import React from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';

interface ComparePanelProps {
    isCompareOpen: boolean;
    setIsCompareOpen: (open: boolean) => void;
    compareList: any[];
    removeFromCompare: (id: string) => void;
    getImageUrl: (url: string) => string;
}

const ComparePanel: React.FC<ComparePanelProps> = ({
    isCompareOpen,
    setIsCompareOpen,
    compareList,
    removeFromCompare,
    getImageUrl
}) => {
    // Use portal to escape framer-motion transform context
    // (framer-motion transforms break fixed positioning of children)
    return ReactDOM.createPortal(
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300 ${
                    isCompareOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
                onClick={() => setIsCompareOpen(false)}
            />

            {/* Slide-in panel from right */}
            <div
                className={`fixed top-0 right-0 h-screen w-full max-w-sm bg-white shadow-2xl z-[9999] flex flex-col transition-transform duration-500 ease-out ${
                    isCompareOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                {/* Header */}
                <div
                    className="p-6 border-b border-gray-100 flex items-center justify-between text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}
                >
                    <div className="flex items-center gap-3">
                        <i className="fa-solid fa-code-compare text-[#f1ae24]"></i>
                        <h5 className="font-bold mb-0 text-white">Compare ({compareList.length}/3)</h5>
                    </div>
                    <button
                        onClick={() => setIsCompareOpen(false)}
                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all border-0 text-white"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {compareList.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                <i className="fa-solid fa-layer-group text-3xl text-gray-400"></i>
                            </div>
                            <h6 className="font-bold text-[#05144b]">No properties added</h6>
                            <p className="text-xs text-gray-500">Add up to 3 properties to compare them side-by-side.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {compareList.map((item: any) => (
                                <div key={item._id} className="group relative bg-white rounded-2xl border border-gray-100 p-2 flex gap-4 hover:shadow-lg transition-all">
                                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                                        <img
                                            src={getImageUrl(item.featuredImage || (item.gallery?.exterior && item.gallery.exterior[0]) || '/assets/img/p-1.jpg')}
                                            className="w-full h-full object-cover"
                                            alt={item.name}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0 py-1">
                                        <h6 className="font-bold text-[#05144b] mb-0.5 truncate text-sm">{item.name}</h6>
                                        <p className="text-[10px] text-gray-500 mb-1 italic truncate">
                                            <i className="fa-solid fa-location-dot mr-1 text-[#f1ae24]"></i>
                                            {item.location}
                                        </p>
                                        <div className="text-[#007bff] font-black text-xs">
                                            {item.overview?.priceRange || item.priceRange || 'On Request'}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeFromCompare(item._id)}
                                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center shadow-sm hover:bg-red-500 hover:text-white transition-all opacity-100 border-0"
                                    >
                                        <i className="fa-solid fa-trash-can text-[10px]"></i>
                                    </button>
                                </div>
                            ))}

                            {compareList.length < 3 && (
                                <Link
                                    to="/project-list"
                                    className="border-2 border-dashed border-gray-100 rounded-2xl p-6 flex flex-col items-center justify-center text-gray-300 gap-1 mt-4 hover:border-[#f1ae24]/30 hover:bg-gray-50 transition-all group/add no-underline"
                                    onClick={() => setIsCompareOpen(false)}
                                >
                                    <i className="fa-solid fa-plus text-lg group-hover/add:scale-110 group-hover/add:text-[#f1ae24] transition-all"></i>
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400 group-hover/add:text-[#05144b] transition-all">Add More</span>
                                </Link>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                    <Link
                        to="/compare-projects"
                        className={`w-full py-4 rounded-2xl font-black text-[10px] tracking-widest transition-all flex items-center justify-center gap-3 no-underline uppercase bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white hover:opacity-90 ${
                            compareList.length < 2 ? 'opacity-50 pointer-events-none' : ''
                        }`}
                        onClick={() => compareList.length >= 2 && setIsCompareOpen(false)}
                    >
                        <i className="fa-solid fa-rocket"></i>
                        COMPARE NOW
                    </Link>
                    {compareList.length < 2 && (
                        <p className="text-[9px] text-center mt-3 text-gray-400 font-bold uppercase tracking-wider">
                            Add min 2 properties
                        </p>
                    )}
                </div>
            </div>
        </>,
        document.body   // Render directly at body level — escapes all transforms
    );
};

export default ComparePanel;
